import {Fragment, ReactNode} from "react";
import {List} from "components/common/list/List.component";
import {ListItem} from "components/common/list/ListItem.component";
import {Tag, TagType, TagTypeValue} from "components/common/Tag.component";
import {useTranslation} from "components/translations/useTranslation";
import {eventBus} from "components/pagelayout/EventBus";
import {Tooltip} from "components/tooltip/Tooltip";
import {SHARED_TOOLTIP_ID} from "components/tooltip/AppTooltip.component";
import {Event, Step} from "types/workflowDetailsWizard";
import {StepListItemProps, TimelineEntry, WorkflowDetailsStepsProps} from "types/workflowDetails";
import {StatusType} from "types/workflowDetailsWizardStep";
import {formatDate} from "utils/valueFormatter.function";
import WorkflowHistoryEvent from "./WorkflowHistoryEvent.component";
import CurrentWorkflowStep, {ApproversTable} from "./CurrentWorkflowStep.component";
import {
    MAX_SUMMARY_NAMES,
    assignmentNames,
    buildTimeline,
    CONFIGURATION_ERROR_TAG,
    executedDate,
    formatDateTime,
    isConfigurationErrorStep,
    isActiveStep,
    joinNameParts,
    outcomeEvent,
    statusFromAction,
    stepKey,
    stepTag
} from "./workflowDetails.function";

// The tag shows the day; hovering it shows the time of day as well. A button, so keyboard users can focus it to
// get the tooltip too; screen readers get the time from a hidden copy, as the shared tooltip has nothing to point to.
// The copy sits outside the button, or it would be read as part of the button's name as well
const DateTag = ({id, type, date}: { id: string, type: TagTypeValue, date: Date | string }) => {
    const tooltipId = `${id}-date-tooltip`;
    const descriptionId = `${id}-date-description`;
    return (
        <>
            <button type="button" className="workflow-step__tooltip-trigger workflow-step__date"
                    data-tip={tooltipId} data-for={SHARED_TOOLTIP_ID} aria-describedby={descriptionId}>
                <Tag type={type} message={formatDate(date, null)}/>
            </button>
            <span id={descriptionId} className="sr-only">{formatDateTime(date)}</span>
            <Tooltip id={tooltipId} content={() => formatDateTime(date)}/>
        </>
    );
}

// Expanding a step changes the panel height, so the grid around it has to re-measure
const refreshGridLayout = () => eventBus.dispatch('@@grid/refreshGridLayout');

const StepListItem = (props: StepListItemProps) => {
    const {translate} = useTranslation();
    const hasErrors = !!props.errors && props.errors.length > 0;
    const hasApprovers = !!props.approvers && props.approvers.length > 0;
    const hasContent = hasErrors || props.events.length > 0 || !!props.currentStep || hasApprovers;
    // Events can name no step at all, e.g. after the workflow was changed
    const isUnspecified = !props.name?.trim();
    const name = isUnspecified ? translate("taskDetail.unspecifiedStep") : props.name;
    const tooltipId = `${props.itemKey}-unspecified-tooltip`;

    return (
        <ListItem className={["workflow-step",
                              props.configurationError && "workflow-step--configuration-error",
                              props.inactive && "workflow-step--inactive"].filter(Boolean).join(" ")}
                  dataCy="workflowDetails.step"
                  title={<>
                      {isUnspecified ?
                          <>
                              <button type="button" className="workflow-step__tooltip-trigger workflow-step__name"
                                      data-tip={tooltipId} data-for={SHARED_TOOLTIP_ID}
                                      aria-describedby={`${props.itemKey}-unspecified-description`}>
                                  {name}
                              </button>
                              <span id={`${props.itemKey}-unspecified-description`} className="sr-only">
                                  {translate("taskDetail.unspecifiedStepTooltip")}
                              </span>
                              <Tooltip id={tooltipId} content={() => translate("taskDetail.unspecifiedStepTooltip")}/>
                          </> :
                          <span className="workflow-step__name">{name}</span>}
                      {props.summary && <span className="workflow-step__summary">{props.summary}</span>}
                  </>}
                  tags={props.tag}
                  toggleAriaLabel={translate("taskDetail.toggleStepDetails", name)}
                  isDisabled={!hasContent}
                  defaultExpanded={!!props.currentStep}
                  onExpandedChange={refreshGridLayout}
                  expandedContent={hasContent ?
                      <div className="workflow-step__content">
                          {hasErrors && props.errors.map((error) =>
                              <div key={error} className="workflow-step__error">{error}</div>)}
                          {props.events.length > 0 &&
                              <List className="workflow-step__events" ariaLabel={translate("taskDetail.workflowHistory")}>
                                  {props.events.map((event, index) =>
                                      <WorkflowHistoryEvent key={`${event.date}-${index}-${event.action}`}
                                                            event={event}
                                                            listedUnderStep={props.stepName}
                                                            uniqueKey={`${props.itemKey}-${index}`}/>)}
                              </List>}
                          {props.currentStep && <CurrentWorkflowStep step={props.currentStep}/>}
                          {hasApprovers &&
                              <div className="current-step" data-cy="workflowDetails.stepApprovers">
                                  <ApproversTable title={translate("taskDetail.stepApprovers")}
                                                  assignments={props.approvers}
                                                  uniqueKey={`${props.itemKey}-approvers`}/>
                              </div>}
                      </div> : null}/>
    );
}

export function WorkflowDetailsSteps({data, events, hideSkippedSteps}: Readonly<WorkflowDetailsStepsProps>) {
    const {translate} = useTranslation();
    const steps = data.process?.steps ?? [];
    // In a summary the users' names stand out from the text around them
    const userName = (name: ReactNode) => <span className="workflow-step__user">{name}</span>;
    const names = (list: Array<string>) =>
        joinNameParts(list, translate("taskDetail.and"), translate("taskDetail.more")).map((part) =>
            <Fragment key={part.key}>{part.isName ? userName(part.text) : part.text}</Fragment>);
    const summary = (text: string, nameList: ReactNode) => <>{text} {nameList}</>;

    const stepSummary = (step: Step): ReactNode => {
        if (isConfigurationErrorStep(step)) {
            return <span className="workflow-step__note">{translate("taskDetail.mandatoryStepNoApprovers")}</span>;
        }
        switch (step.status) {
            case StatusType.PASSED:
            case StatusType.APPROVED:
                return step.completedAssignments.length > 0 &&
                    summary(translate("taskDetail.approvedBy"), names(assignmentNames(step.completedAssignments)));
            case StatusType.ACTIVE:
            case StatusType.CURRENT:
                return step.incompleteAssignments.length > 0 &&
                    summary(translate("taskDetail.pendingApprovalBy"), names(assignmentNames(step.incompleteAssignments)));
            case StatusType.FUTURE:
                return step.incompleteAssignments.length > 0 &&
                    summary(translate("taskDetail.tasksFor"), names(assignmentNames(step.incompleteAssignments)));
            case StatusType.REJECTED:
                return step.lastActionBy ?
                    summary(translate("taskDetail.rejectedBy"), userName(`${step.lastActionBy.firstName} ${step.lastActionBy.lastName}`)) :
                    translate("workflowProcessStatus.REJECTED");
            case StatusType.CANCELED:
                return data.displaySourceApplication &&
                    `${translate("workflowProcessStatus.CANCELLED")} ${translate("taskDetail.from")} ${data.displaySourceApplication}`;
            case StatusType.SKIPPED:
                return step.explanation;
            default:
                return null;
        }
    }

    // An executed step's tag shows when it was acted on instead of its status, which the tag's type already shows
    const tag = (id: string, step: Step, stepEvents: Array<Event> = []) => {
        const statusTag = isConfigurationErrorStep(step) ? CONFIGURATION_ERROR_TAG : stepTag(step.status);
        if (!statusTag) return null;
        const date = executedDate(step, stepEvents);
        return date ?
            <DateTag id={id} type={statusTag.type} date={date}/> :
            <Tag type={statusTag.type} message={translate(statusTag.translationKey)}/>;
    }

    const statusTag = (id: string, event?: Event, stepEvents?: Array<Event>) => {
        const status = statusFromAction(event);
        return status && tag(id, {status} as Step, stepEvents);
    }

    // A START event names who started its run (user, or only fullUserName); without one the run is the current one
    const initiatorName = (startEvent?: Event) => {
        const initiatedBy = startEvent ? startEvent.user : data.requester || data.submitter;
        return initiatedBy ? `${initiatedBy.firstName} ${initiatedBy.lastName}` : startEvent?.fullUserName;
    }

    // When the run was started: its START event, or for the current run without one, when the workflow was received
    const initiatedTag = (id: string, startEvent: Event | undefined, isCurrentRun: boolean) => {
        const date = startEvent ? startEvent.date : isCurrentRun && data.received;
        return date ?
            <DateTag id={id} type={TagType.SUCCESS} date={date}/> :
            <Tag type={TagType.SUCCESS} message={translate("taskDetail.activated")}/>;
    }

    const initiatedSummary = (startEvent?: Event) => {
        const initiatedBy = initiatorName(startEvent);
        const application = startEvent?.displayApplication || startEvent?.application || data.displaySourceApplication;
        return initiatedBy ?
            <>{userName(initiatedBy)} {translate("taskDetail.activatedFrom")} {application}</> :
            `${translate("taskDetail.activatedFromNoUser")} ${application}`;
    }

    const visible = (list: Array<Step>) => list.filter((step) => !hideSkippedSteps || step.status !== StatusType.SKIPPED);

    const timeline = buildTimeline(events, steps);
    // Only the latest run is the current one; an earlier run without a START was not started when this one was received
    const initiatedKeys = timeline.filter((entry) => entry.kind === "initiated").map((entry) => entry.key);
    const currentRunKey = initiatedKeys[initiatedKeys.length - 1];

    const renderEntry = (entry: TimelineEntry) => {
        switch (entry.kind) {
            case "initiated": {
                const startEvent = entry.events.find((event) => event.eventType === "START");
                return <StepListItem key={entry.key}
                                     itemKey={entry.key}
                                     name={translate(entry.key === initiatedKeys[0] ?
                                         "taskDetail.workflowInitiated" : "taskDetail.workflowReinitiated")}
                                     summary={initiatedSummary(startEvent)}
                                     tag={initiatedTag(entry.key, startEvent, entry.key === currentRunKey)}
                                     events={entry.events}/>;
            }
            case "steps":
                return <Fragment key={entry.key}>
                    {/* ListItem reads defaultExpanded only on mount, so a step that becomes (or stops being) the
                        active one is remounted to open (or close) it; the other rows keep what the user expanded */}
                    {visible(entry.steps).map((step) => {
                        const configurationError = isConfigurationErrorStep(step);
                        const stepEvents = entry.eventsByStep.get(stepKey(step)) ?? [];
                        return <StepListItem key={`${stepKey(step)}${isActiveStep(step) ? "-active" : ""}`}
                                             itemKey={stepKey(step)}
                                             name={step.name}
                                             stepName={step.name}
                                             summary={stepSummary(step)}
                                             tag={tag(stepKey(step), step, stepEvents)}
                                             // the summary already says what is wrong with a configuration error step
                                             errors={configurationError ? undefined : step.errorExplanations}
                                             configurationError={configurationError}
                                             inactive={step.status === StatusType.FUTURE || step.status === StatusType.SKIPPED}
                                             events={stepEvents}
                                             // with no approvers there is nobody for the step to await approval from
                                             currentStep={isActiveStep(step) && !configurationError ? step : undefined}
                                             // the summary shortens them to "N more", so a future step lists them all
                                             approvers={step.status === StatusType.FUTURE &&
                                                 step.incompleteAssignments.length > MAX_SUMMARY_NAMES ?
                                                 step.incompleteAssignments : undefined}/>;
                    })}
                    {steps.length === 0 && data.status === "CANCELED" &&
                        <StepListItem itemKey="no-workflow-canceled"
                                      name={translate("workflowProcessStatus.CANCELLED")}
                                      summary={null}
                                      tag={tag("no-workflow-canceled", {status: StatusType.CANCELED} as Step)}
                                      errors={[translate("taskDetail.noWorkflowDocument")]}
                                      events={[]}/>}
                </Fragment>;
            case "pastStep": {
                // the tag shows the step's outcome and when it was reached, not what was logged after it
                const outcome = outcomeEvent(entry.events);
                return <StepListItem key={entry.key}
                                     itemKey={entry.key}
                                     name={entry.name}
                                     stepName={entry.name}
                                     summary={null}
                                     tag={outcome && statusTag(entry.key, outcome, [outcome])}
                                     events={entry.events}/>;
            }
            case "completed":
                return <StepListItem key={entry.key}
                                     itemKey={entry.key}
                                     name={translate(entry.events[0].action === "CANCELED" ?
                                         "taskDetail.workflowCanceled" : "taskDetail.workflowCompleted")}
                                     summary={null}
                                     // the date the END event closed the run, not of what was logged after it
                                     tag={statusTag(entry.key, entry.events[0], [entry.events[0]])}
                                     events={entry.events}/>;
        }
    }

    return (
        <List className="workflow-steps" ariaLabel={translate("taskDetail.workflowDetails")}
              dataCy="workflowDetails.steps">
            {timeline.map(renderEntry)}
        </List>
    );
}

export default WorkflowDetailsSteps;
